import { useEffect, useMemo, useState, useRef } from 'react'
import { withRouter } from 'react-router'
import { graphql } from '@apollo/client/react/hoc'
import { branch, compose, renderNothing, setDisplayName } from 'react-recompose'
import queryString from 'query-string'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
  apply,
  either,
  getPath,
  not,
  pick,
  pipe,
  propEquals,
} from '@anewgo/functions'
import { IPAD_PORTRAIT, siteplans } from '@anewgo/utils'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  determineIfSelectionHasReservation,
  determineIfSelectionHasSignedReservation,
  getInventoryImage,
} from '../../utils'
import {
  CHOOSE_LOT_AND_PLAN,
  MOBILE_PRESENTATION_MODE,
} from '@anewgo/interactive-siteplan-core'

import ModalDrawer from '../ModalDrawer/ModalDrawer'
import {
  BUILDER_APP_CONFIG,
  COMMUNITY_PLANS,
  SITEPLAN_BY_COMMUNITY_ID,
  SITEPLAN_BY_ID,
  LOT_SITEPLAN_BY_ID,
  SITEPLAN_LITE_BY_COMMUNITY_ID,
  SITEPLAN_LITE_BY_ID,
  ELEVATION_COLORS,
  ELEVATION_COMMUNITIES,
} from '../../graphql'
import { HEADER_HT } from '../../constants'
import { apolloClient } from '../../graphql/enhancers'
import { StoreConsumer } from '../../store'
import * as reducers from '../../store/reducers'
import { selectionLinkUrl } from '../../utils'
import { useEventTracker } from '../../utils/hooks'
import {
  fromRenderProps,
  graphqlProps,
  withBuilderAppConfig,
  withClient,
  withCommunity,
  withActivePlanElevation,
} from '../../hocs'
import { requireProps } from '../helper'

import ExpandedSelection from '../ExpandedSelection/ExpandedSelection'
import ProspectSgtAppointmentsDialog from '../ProspectSgtAppointmentsDialog/ProspectSgtAppointmentsDialog'
import { showButton } from '../ReserveButton/ReserveButton'
import gql from 'graphql-tag'
import { LOT_UPDATE } from '../../graphql/nexusSubscription'
import { useSubscription } from '@apollo/client'
import InteractiveSiteplan from '@anewgo/siteplan-ng'
import LotStatus from '../../constants/lotStatus'
import MirrorPlanDialog from './MirrorPlanDialog'
import { CHOOSE_LOT } from '@anewgo/interactive-siteplan-core'

const { isInventoryLot, availableSchemesForLot } = siteplans

const getSelectionGaragePosition = (selection) => {
  return selection.isMirrored
    ? selection.elevation.garagePosition === 'LEFT'
      ? 'RIGHT'
      : 'LEFT'
    : selection.elevation.garagePosition
}

// Compare if elevation/inventory has same garage position as lot
// Otherwise mark it as excluded
const getExcludedByLotGaragePosition = (lot, selection, siteplanMode) => {
  if (
    selection.elevation.svgMirroring ||
    !!selection?.elevation?.mirrorElevationId ||
    siteplanMode !== CHOOSE_LOT ||
    lot?.salesStatus !== LotStatus.Available ||
    lot?.garagePosition === null ||
    selection?.elevation?.garagePosition === null
  ) {
    return false
  }

  return lot.garagePosition !== getSelectionGaragePosition(selection)
}

const InteractiveSiteplanWrapper = compose(
  withRouter,
  withClient,
  withBuilderAppConfig,
  withCommunity(COMMUNITY_PLANS),
  withActivePlanElevation,

  fromRenderProps(
    StoreConsumer,
    pick('dispatch', 'selection', 'prospect', 'uiConfig')
  ),

  fromRenderProps(ExpandedSelection, {
    injectProps: ({ selection }) => ({
      selection,
      loadLotSiteplanInfo: true,
    }),
    mapRenderProps:
      ({ selection }) =>
      (loadedSelection) => ({
        selection: {
          ...selection,
          ...loadedSelection,
        },
      }),
  }),

  // This is for the faster first load
  // This query contains subSiteplans as opposed to SITEPLAN_LITE_BY_ID, which means that
  // a master siteplan will require siteplanId to be null.
  graphql(SITEPLAN_LITE_BY_COMMUNITY_ID, {
    skip: ({ community, siteplanId }) => !community?.id || siteplanId,
    // This is a useful one to cache, even though we don't need the lots quite yet
    options: ({ match, community }) => ({
      variables: {
        clientName: match.params.clientName,
        communityId: community.id,
      },
    }),
    props: graphqlProps('activeSiteplanByCommunityId', {
      mapResultsToProps: (data) => ({
        siteplan: data,
      }),
    }),
  }),

  // This is for the faster first load
  graphql(SITEPLAN_LITE_BY_ID, {
    skip: ({ siteplanId }) => !siteplanId,
    options: ({ match, siteplanId }) => ({
      variables: {
        clientName: match.params.clientName,
        id: +siteplanId,
      },
    }),
    props: graphqlProps('siteplanById', {
      mapResultsToProps: (data) => ({
        siteplan: data,
      }),
    }),
  }),

  // This is for the full set of data that we'll need to complete user interaction.
  // This query contains subSiteplans as opposed to SITEPLAN_LITE_BY_ID, which means that
  // a master siteplan will require siteplanId to be null.
  graphql(SITEPLAN_BY_COMMUNITY_ID, {
    skip: ({ community, siteplanId }) => !community?.id || siteplanId,
    options: ({ match, community }) => ({
      variables: {
        clientName: match.params.clientName,
        communityId: community.id,
      },
    }),
    props: ({ data: { activeSiteplanByCommunityId, loading, error } }) => {
      if (loading) return { loadingFullSiteplan: true }
      if (error) return { error }
      return { siteplan: activeSiteplanByCommunityId }
    },
  }),

  // This is for the full set of data that we'll need to complete user interaction.
  graphql(SITEPLAN_BY_ID, {
    skip: ({ siteplanId }) => !siteplanId,
    options: ({ match, siteplanId }) => ({
      variables: {
        clientName: match.params.clientName,
        id: +siteplanId,
      },
    }),
    props: ({ data: { siteplanById, loading, error } }) => {
      if (loading) return { loadingFullSiteplan: true }
      if (error) return { error }
      return { siteplan: siteplanById }
    },
  }),

  // second query by id

  // This query is for the siteplan of the selected lot -- not necessarily the same
  // siteplan being viewed.
  graphql(LOT_SITEPLAN_BY_ID, {
    skip: either(
      // We can't run the query without community data.
      not(getPath('community')),
      // We should also wait to run the query until after the lot query has finished
      // (if it needed to run). If the siteplanId key exists on the lot,
      // we are ready to run the query.
      ({ selection }) => !selection.lot?.siteplanInfo?.siteplanId
    ),
    options: ({ match, selection }) => ({
      variables: {
        clientName: match.params.clientName,
        id: selection.lot.siteplanInfo.siteplanId,
      },
    }),
    props: graphqlProps('siteplanById', {
      mapResultsToProps: (siteplan) => ({
        siteplanOfSelectedLot: siteplan,
      }),
    }),
  }),

  graphql(BUILDER_APP_CONFIG, {
    options: ({ match }) => ({
      variables: {
        clientName: match.params.clientName,
      },
    }),
    props: graphqlProps('builderAppConfig', {
      optional: true,
    }),
  }),

  requireProps({
    keys: ['client', 'siteplan', 'community'],
    loadingSpinner: true,
  }),
  branch(
    // If a plan and elevation are available in the route, expect to have a plan
    // object from the PlanElevation context to filter by
    ({ planName, elevation, location, error }) => {
      const elevationId = pipe(queryString.parse, ({ elevId }) =>
        elevId === undefined ? undefined : parseInt(elevId, 10)
      )(location.search)

      return (planName && elevationId && !elevation) || error
    },
    renderNothing
  ),

  setDisplayName('InteractiveSiteplanWrapper')
)(
  ({
    backButtonProps,
    builderAppConfig,
    client,
    community,
    dispatch,
    elevation: elevationFromRoute,
    history,
    loadingFullSiteplan,
    prospect,
    selection: originalSelection,
    siteplan,
    siteplanOfSelectedLot,
    uiConfig,
    zoomToSelectedLot,
    plan,
    siteplanMode,
    disableScroll,
    masterSiteplan,
    onSubSiteplanSelect,
  }) => {
    const matchIPadPortrait = useMediaQuery(IPAD_PORTRAIT)
    const eventTracker = useEventTracker()
    const [constructionStatusExclusions, setConstructionStatusExclusions] =
      useState([])
    const [appointmentLot, setAppointmentLot] = useState(false)
    const [lotPremiumRange, setLotPremiumRange] = useState([])
    const [lotSizeRange, setLotSizeRange] = useState([])
    const [openDrawer, setOpenDrawer] = useState(false)
    const [mirrorPlanDialogLot, setMirrorPlanDialogLot] = useState(null)
    const drawerContentRef = useRef(null)
    const selection = {
      ...originalSelection,
      community,
      siteplan: siteplanOfSelectedLot,
    }
    // To determine whether or not to allow further selections
    const onlineReservations = prospect?.onlineReservations
    const favoriteId = selection?.id
    const { closingDateFilter, mobile, planNameFilter, mirror } = uiConfig

    // Subscriptions
    const { data: updatedLot } = useSubscription(LOT_UPDATE, {
      variables: {
        clientName: client.altName,
      },
    })

    useEffect(() => {
      if (updatedLot) {
        const { lotId, salesStatus } = updatedLot?.getLotSalesStatus
        setLotSalesStatus(lotId, salesStatus)
      }
    }, [updatedLot])

    const mobileMenuStyle =
      !uiConfig.mobile && !uiConfig.small
        ? {
            marginTop: HEADER_HT,
            borderTop: '1px solid #bcbcbc',
            borderBottom: '1px solid #222',
          }
        : {}
    const selectionIsAnInventory = apply(isInventoryLot)(selection.lot)

    const showOriginalStatusesForExcludedLots = getPath(
      'page',
      'siteplan',
      'showOriginalStatusesForExcludedLots'
    )(builderAppConfig)

    const originalShowExcludedInventoryLots = getPath(
      'page',
      'siteplan',
      'showExcludedInventoryLots'
    )(builderAppConfig)
    const showExcludedInventoryLots =
      originalShowExcludedInventoryLots === undefined
        ? true
        : originalShowExcludedInventoryLots

    const disabledLotColorModeFromConfig = getPath(
      'page',
      'siteplan',
      'disabled_lot_color_mode'
    )(builderAppConfig)

    const loading = uiConfig && uiConfig.loading

    useEffect(() => {
      if (loadingFullSiteplan && !uiConfig.loading) {
        dispatch(reducers.setLoading(true))
      } else if (!loadingFullSiteplan && uiConfig.loading) {
        dispatch(reducers.setLoading(false))
      }

      return () => {
        if (uiConfig.loading) {
          dispatch(reducers.setLoading(false))
        }
      }
    }, [dispatch, loadingFullSiteplan, loading])

    const handleClearSelection = (evt) => {
      if (
        determineIfSelectionHasSignedReservation(onlineReservations, favoriteId)
      ) {
        dispatch(reducers.setSelectionLockedDialogOpen(true))
        return
      } else if (
        determineIfSelectionHasReservation(onlineReservations, favoriteId)
      ) {
        dispatch(
          reducers.setSnackBar({
            open: true,
            message:
              'This lot cannot be deselected because a reservation is in progress on this lot.',
            type: 'warning',
          })
        )
        return
      }
      if (elevationFromRoute) {
        // We can't clear the plan/elevation if we're on a floorplan route
        dispatch(reducers.clearHomesite())
        dispatch(reducers.updateLastUpdate(new Date().getTime()))
      } else {
        dispatch(reducers.clearPlanElevationAndHomesite())
      }
    }

    const handleClosingDateChange = (value) => {
      if (value) {
        dispatch(reducers.assignClosingDateFilter({ daysOut: value }))
      } else {
        dispatch(reducers.resetClosingDateFilter())
      }
    }

    const handlePlanFilterChange = (value) => {
      const plan = community.plans.find((plan) => plan.id === value)
      dispatch(reducers.setPlanNameFilter(plan))
    }

    const shouldExcludeLot = (lot) => {
      if (!elevationFromRoute || showOriginalStatusesForExcludedLots) {
        return false
      }

      let salesStatuses = LotStatus.getInventoryStatuses()
      salesStatuses.push(LotStatus.Available)

      const excludableSalesStatus = salesStatuses.includes(lot.salesStatus)

      const excludedPlanElevations = getPath('excludedPlanElevations')(lot)

      let isExcludedByLotGaragePosition = getExcludedByLotGaragePosition(
        lot,
        selection,
        siteplanMode
      )

      return (
        (excludedPlanElevations?.some(
          propEquals('elevationId', elevationFromRoute.id)
        ) &&
          excludableSalesStatus &&
          !(showExcludedInventoryLots && isInventoryLot(lot))) ||
        isExcludedByLotGaragePosition
      )
    }

    const inventoryImg = useMemo(
      () => {
        if (appointmentLot) {
          const inventoryElevation = getPath(
            'inventory',
            'elevation'
          )(appointmentLot)
          const scaleImg = false
          return getInventoryImage(
            appointmentLot.inventory,
            inventoryElevation,
            client.directoryName,
            scaleImg
          )
        }
        return null
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [appointmentLot]
    )

    const onSelection = async (
      object,
      applyGarageValidation = true,
      flipMirroring = false
    ) => {
      if (
        determineIfSelectionHasSignedReservation(onlineReservations, favoriteId)
      ) {
        if (!object.lot || !isInventoryLot(object.lot)) {
          dispatch(reducers.setSelectionLockedDialogOpen(true))
          return
        }
      }

      if (object.lot.salesStatus === LotStatus.BeingReserved) {
        dispatch(reducers.setLotIsBeingReservedDialogOpen(true))
        return
      }

      if (
        siteplanMode === CHOOSE_LOT_AND_PLAN &&
        object.elevation.svgMirroring &&
        object.lot.garagePosition &&
        object.elevation.garagePosition &&
        object.lot.garagePosition !== object.elevation.garagePosition
      ) {
        flipMirroring = selection.elevation
          ? object.lot.garagePosition !== selection.elevation.garagePosition
          : true
      }

      if (
        siteplanMode === CHOOSE_LOT &&
        selection?.elevation &&
        applyGarageValidation &&
        selection?.elevation?.svgMirroring &&
        object.lot.garagePosition &&
        selection.elevation.garagePosition &&
        object.lot.garagePosition !== getSelectionGaragePosition(selection)
      ) {
        setMirrorPlanDialogLot(object)
        return
      }

      const availableSchemes = availableSchemesForLot(object.lot) || []

      let schemeForLot
      if (availableSchemes?.length && availableSchemes.length === 1) {
        const schemeId = availableSchemes[0].id
        const queryResult = await apolloClient.query({
          query: ELEVATION_COLORS,
          variables: {
            clientName: client.altName,
            elevationId: elevationFromRoute?.id || object?.elevation?.id,
            planId: plan?.id || object?.plan?.id,
            communityId: community.id,
          },
          fetchPolicy: 'cache-first',
        })
        schemeForLot = queryResult.data.elevation.schemes.find(
          (scheme) => scheme.id === schemeId
        )
      }

      const queryResult = await apolloClient.query({
        query: ELEVATION_COMMUNITIES,
        variables: {
          clientName: client.altName,
          elevationId: elevationFromRoute?.id || object?.elevation?.id,
          planId: plan?.id || object?.plan?.id,
        },
        fetchPolicy: 'cache-first',
      })

      const { plan: planFromObject, lot } = object || {}
      const elevation = { ...object.elevation, ...queryResult.data.elevation }

      dispatch(
        reducers.selectHome({
          ...object,
          mirror: flipMirroring ? !mirror : mirror,
          schemeForLot,
          client,
          community,
          elevation,
        })
      )
      dispatch(reducers.updateLastUpdate(new Date().getTime()))

      if (selection.elevation?.svgMirroring) {
        dispatch(
          reducers.setSvgMirrored(
            flipMirroring ? !selection.isMirrored : selection.isMirrored
          )
        )
      }

      if (
        (mobile || siteplanMode === 'CHOOSE_LOT') &&
        ((elevation && planFromObject) || (lot && selectionIsAnInventory))
      ) {
        handleLink({ elevation, plan: planFromObject, lot })
      }
    }

    const setLotSalesStatus = (lotId, salesStatus) => {
      apolloClient.writeFragment({
        id: `Lot:${lotId}`,
        fragment: gql`
          fragment Lot on Lot {
            salesStatus
          }
        `,
        data: {
          salesStatus,
        },
      })
    }

    const handleLink = ({ plan, elevation, lot }) => {
      history.push(
        selectionLinkUrl({
          client,
          community: selection.community,
          plan,
          elevation,
          lot,
        })
      )
    }

    function mapColorSelections(colorSelections) {
      let colorSelectionsArray = []

      Object.keys(colorSelections).forEach((elementId) => {
        const { id, paletteId } = colorSelections[elementId]
        colorSelectionsArray?.push({
          paletteId,
          id: id,
          materialId: parseInt(elementId),
        })
      })
    }

    function mapSelection(selection) {
      return {
        communityId: selection.community?.id,
        planId: selection.plan?.id,
        elevationId: selection.elevation?.id,
        inventoryId: selection.lot?.inventory?.id,
        lotId: selection.lot?.id,
        exteriorSelection: {
          schemeId: selection.scheme?.id,
          selections: mapColorSelections(selection.colorSelections),
        },
        siteplan: selection.siteplan,
      }
    }

    return (
      <>
        <MirrorPlanDialog
          isOpen={!!mirrorPlanDialogLot}
          onClose={() => setMirrorPlanDialogLot(null)}
          elevationGaragePosition={selection?.elevation?.garagePosition}
          onSelection={onSelection}
          selectedLotObject={mirrorPlanDialogLot}
        />
        <div
          style={
            !disableScroll
              ? { overflow: 'auto', height: '100%' }
              : { height: '100%' }
          }
        >
          <InteractiveSiteplan
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_KEY}
            disabledLotColorMode={
              disabledLotColorModeFromConfig?.[0].toUpperCase() +
              disabledLotColorModeFromConfig?.substr(1)
            }
            eventTracking={eventTracker}
            mobile={matchIPadPortrait ? MOBILE_PRESENTATION_MODE : mobile}
            onClearSelection={handleClearSelection}
            siteplanMode={siteplanMode}
            onSelection={onSelection}
            selection={mapSelection(selection)}
            getSelectionData={() => selection}
            shouldExcludeLot={shouldExcludeLot}
            siteplan={{
              ...siteplan,
              siteplanType: siteplan?.geoInfo ? 'GEO' : 'IMAGE',
            }}
            zoomToSelectedLot={zoomToSelectedLot}
            backToMasterSiteplanButton={backButtonProps}
            selectedLotInfoButton={{
              title: selectionIsAnInventory ? 'Inventory' : 'Floorplan',
              onSelectedLotInfoButtonClick: (selection) => {
                const { plan, elevation, lot } = selection
                handleLink({ plan, elevation, lot })
              },
            }}
            buyNowButton={showButton}
            directoryName={client.directoryName}
            excludedLotsDisplayVariant="hidden"
            filters={{
              planFilter: planNameFilter?.id,
              onPlanFilterChange: (value) => handlePlanFilterChange(value),
              closingDateFilter: closingDateFilter?.daysOut,
              onClosingDateChange: (value) => handleClosingDateChange(value),
              lotSizeRangeFilter: lotSizeRange,
              onLotSizeRangeFilterChange: (value) => setLotSizeRange(value),
              lotPremiumRangeFilter: lotPremiumRange,
              onLotPremiumRangeFilterChange: (value) =>
                setLotPremiumRange(value),
              constructionStatusFilter: constructionStatusExclusions,
              onConstructionStatusFilterChange: (value) =>
                setConstructionStatusExclusions(value),
            }}
            schemes={community.schemes}
            lots={siteplan.lots}
            plans={community.plans}
            community={community}
            masterSiteplan={masterSiteplan}
            onSubSiteplanSelect={onSubSiteplanSelect}
          />
          {appointmentLot && (
            <ProspectSgtAppointmentsDialog
              isOpen={appointmentLot}
              setOpen={setAppointmentLot}
              client={client}
              agentEmail={community.agents.map((agent) => agent.email)}
              imgUrl={inventoryImg}
              lot={appointmentLot}
              community={community}
            />
          )}
          <ModalDrawer
            openDrawer={openDrawer}
            setOpenDrawer={setOpenDrawer}
            topDrawer={true}
            slideFrom={'top'}
            style={mobileMenuStyle}
          >
            <div ref={drawerContentRef} />
          </ModalDrawer>
        </div>
      </>
    )
  }
)

export default InteractiveSiteplanWrapper
